'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import SiteHeader from '@/components/SiteHeader';
import SiteFooter from '@/components/SiteFooter';
import { Home, Building2, Plane, Users, Phone, Mail, ArrowRight, AlertCircle, Loader2, TrendingUp, Key } from 'lucide-react';

type PageState = 'loading' | 'populated' | 'unavailable';

interface MockListing {
  id: string;
  label: string;
  price: string;
  summary: string;
  type: 'Tower' | 'Townhome';
  status: 'Available' | 'Under Contract' | 'Recently Sold';
  statusAccent: string;
  accent: string;
}

const mockListings: MockListing[] = [
  {
    id: 'listing-1',
    label: 'Tower 1 · High Floor',
    price: '$650,000',
    summary: '2 bed · 2 bath · 1,420 sqft · Strip & Canyon views',
    type: 'Tower',
    status: 'Available',
    statusAccent: '#4CC9FF',
    accent: '#4CC9FF',
  },
  {
    id: 'listing-2',
    label: 'Tower 2 · Mid Floor',
    price: '$485,000',
    summary: '1 bed · 1 bath · 980 sqft · City views',
    type: 'Tower',
    status: 'Under Contract',
    statusAccent: '#FF3DF5',
    accent: '#7A5CFF',
  },
  {
    id: 'listing-3',
    label: 'Townhome · Ground Level',
    price: '$720,000',
    summary: '3 bed · 2.5 bath · 1,850 sqft · Private entry',
    type: 'Townhome',
    status: 'Available',
    statusAccent: '#4CC9FF',
    accent: '#FF3DF5',
  },
  {
    id: 'listing-4',
    label: 'Tower 1 · Penthouse Floor',
    price: '$1,100,000',
    summary: '3 bed · 3 bath · 2,200 sqft · Panoramic views',
    type: 'Tower',
    status: 'Available',
    statusAccent: '#4CC9FF',
    accent: '#4CC9FF',
  },
  {
    id: 'listing-5',
    label: 'Tower 2 · Upper Floor',
    price: '$3,200/mo',
    summary: '2 bed · 2 bath · 1,280 sqft · Rental opportunity',
    type: 'Tower',
    status: 'Available',
    statusAccent: '#4CC9FF',
    accent: '#7A5CFF',
  },
  {
    id: 'listing-6',
    label: 'Townhome · Corner Unit',
    price: '$4,800/mo',
    summary: '3 bed · 3 bath · 2,100 sqft · Rental · Private patio',
    type: 'Townhome',
    status: 'Recently Sold',
    statusAccent: '#A7A3C4',
    accent: '#FF3DF5',
  },
];

const residentTypes = [
  {
    id: 'owners',
    icon: Key,
    title: 'Ownership at Panorama',
    body: 'Panorama ownership offers long-term value in one of Las Vegas\u2019s most recognized residential addresses. Tower units and townhomes represent different price points and lifestyle preferences within the same premium community.',
    accent: '#4CC9FF',
  },
  {
    id: 'renters',
    icon: Home,
    title: 'Luxury Rental Appeal',
    body: 'Renting at Panorama means access to the full amenity experience, a genuine community, and a premium Las Vegas address \u2014 without the commitment of ownership. Renters are full members of the Panorama community.',
    accent: '#7A5CFF',
  },
  {
    id: 'second-home',
    icon: Plane,
    title: 'Second-Home Ownership',
    body: 'Many Panorama owners use it as a Las Vegas base \u2014 for business, entertainment, or simply having a premium address in the city. The lock-and-leave lifestyle is purpose-built into the building.',
    accent: '#FF3DF5',
  },
];

const marketSnapshot = [
  { label: 'Avg. Tower Sale Price', value: '$580K', trend: '+4.2%', accent: '#4CC9FF' },
  { label: 'Avg. Townhome Price', value: '$740K', trend: '+2.8%', accent: '#7A5CFF' },
  { label: 'Avg. Rental Rate', value: '$3,400/mo', trend: '+5.1%', accent: '#FF3DF5' },
  { label: 'Days on Market (Avg)', value: '38 days', trend: '-12%', accent: '#4CC9FF' },
];

export default function RealEstatePage() {
  const [pageState, setPageState] = useState<PageState>('loading');
  const [activeFilter, setActiveFilter] = useState<string>('All');

  useEffect(() => {
    const t = setTimeout(() => setPageState('populated'), 900);
    return () => clearTimeout(t);
  }, []);

  const filters = ['All', 'Tower', 'Townhome'];
  const filteredListings = activeFilter === 'All' ? mockListings : mockListings.filter((l) => l.type === activeFilter);

  return (
    <div className="min-h-screen" style={{ background: '#07060C' }}>
      <SiteHeader theme="transparent" />
      <main>
        {/* Hero */}
        <section className="relative pt-32 pb-20 overflow-hidden panorama-hero-bg">
          <div
            className="absolute top-0 right-0 w-[55%] h-[75%] pointer-events-none"
            style={{ background: 'radial-gradient(ellipse 70% 65% at 100% 5%, rgba(76,201,255,0.18) 0%, rgba(122,92,255,0.08) 50%, transparent 70%)' }}
          />
          <div className="relative z-10 max-w-screen-2xl mx-auto px-6 lg:px-10">
            <div
              className="inline-flex items-center gap-2 px-3 py-1.5 mb-6"
              style={{ borderRadius: '6px', background: 'rgba(76,201,255,0.10)', border: '1px solid rgba(76,201,255,0.22)' }}
            >
              <Building2 size={12} style={{ color: '#4CC9FF' }} />
              <span className="text-[12px] font-medium tracking-wide" style={{ color: '#4CC9FF' }}>
                Real Estate at Panorama
              </span>
            </div>
            <h1
              className="font-display font-extrabold text-[48px] sm:text-[60px] lg:text-[68px] leading-[1.04] tracking-tight mb-5 max-w-3xl"
              style={{ color: '#F2F4FF' }}
            >
              Own, rent, or invest{' '}
              <span className="text-transparent bg-clip-text" style={{ backgroundImage: 'linear-gradient(135deg, #4CC9FF 0%, #7A5CFF 100%)' }}>
                at Panorama.
              </span>
            </h1>
            <p className="text-[18px] leading-relaxed max-w-2xl mb-10 font-light" style={{ color: 'rgba(242,244,255,0.72)' }}>
              Panorama Towers offers a range of ownership and rental opportunities across tower residences and townhomes. This is a property overview — a starting point for exploring what's possible here.
            </p>
            <p className="text-[13px] mb-8" style={{ color: 'rgba(167,163,196,0.45)' }}>
              Listing data shown is illustrative. Contact a Panorama specialist for current availability and pricing.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link href="/request-access" className="btn-primary group">
                Request Access
                <ArrowRight size={15} className="transition-transform duration-200 group-hover:translate-x-0.5" />
              </Link>
              <Link href="/why-panorama" className="btn-secondary">
                Why Panorama
              </Link>
            </div>
          </div>
          <div className="absolute bottom-0 left-0 right-0 neon-divider" />
        </section>

        {/* Loading */}
        {pageState === 'loading' && (
          <section className="py-24" style={{ background: '#0C0A16' }}>
            <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 flex flex-col items-center justify-center py-20 gap-4">
              <Loader2 size={32} className="animate-spin" style={{ color: '#4CC9FF' }} />
              <p className="text-[15px]" style={{ color: '#A7A3C4' }}>Loading…</p>
            </div>
          </section>
        )}

        {/* Unavailable */}
        {pageState === 'unavailable' && (
          <section className="py-24" style={{ background: '#0C0A16' }}>
            <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 flex flex-col items-center justify-center py-20 gap-4 text-center">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: 'rgba(255,61,245,0.10)', border: '1px solid rgba(255,61,245,0.20)' }}>
                <AlertCircle size={22} style={{ color: '#FF3DF5' }} />
              </div>
              <h3 className="font-display font-semibold text-[20px]" style={{ color: '#F2F4FF' }}>Content unavailable</h3>
              <p className="text-[14px] max-w-sm" style={{ color: '#A7A3C4' }}>We're having trouble loading this page. Please try again shortly.</p>
              <Link href="/" className="btn-secondary text-[13px] px-4 py-2 mt-2">Return Home</Link>
            </div>
          </section>
        )}

        {/* Populated */}
        {pageState === 'populated' && (
          <>
            {/* Market snapshot */}
            <section className="py-16" style={{ background: '#0C0A16' }}>
              <div className="max-w-screen-2xl mx-auto px-6 lg:px-10">
                <div className="flex items-center gap-3 mb-8">
                  <TrendingUp size={18} style={{ color: '#4CC9FF' }} />
                  <h2 className="font-display font-bold text-[22px]" style={{ color: '#F2F4FF' }}>Market Snapshot</h2>
                  <span className="text-[11px] px-2 py-0.5 rounded" style={{ background: 'rgba(255,255,255,0.04)', color: 'rgba(167,163,196,0.50)', border: '1px solid rgba(255,255,255,0.07)' }}>
                    Illustrative · Not live data
                  </span>
                </div>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  {marketSnapshot.map((item) => (
                    <div key={item.label} className="neon-card p-5 text-center">
                      <div className="font-display font-bold text-[24px] mb-1" style={{ color: item.accent }}>
                        {item.value}
                      </div>
                      <div className="text-[12px] mb-2" style={{ color: '#A7A3C4' }}>{item.label}</div>
                      <div className="text-[11px] font-medium" style={{ color: item.trend.startsWith('+') ? '#4CC9FF' : '#FF3DF5' }}>
                        {item.trend} YoY
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* Listings */}
            <section className="py-16" style={{ background: '#07060C' }}>
              <div className="neon-divider mb-0" />
              <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 pt-16">
                <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
                  <div>
                    <h2 className="font-display font-bold text-[28px]" style={{ color: '#F2F4FF' }}>
                      Overview Listings
                    </h2>
                    <p className="text-[13px] mt-1" style={{ color: 'rgba(167,163,196,0.55)' }}>
                      Illustrative examples — contact a specialist for current availability
                    </p>
                  </div>
                  <div className="flex gap-2">
                    {filters.map((f) => (
                      <button
                        key={f}
                        onClick={() => setActiveFilter(f)}
                        className="px-3.5 py-1.5 rounded-lg text-[12px] font-medium transition-all duration-150"
                        style={activeFilter === f
                          ? { background: 'rgba(76,201,255,0.12)', color: '#4CC9FF', border: '1px solid rgba(76,201,255,0.25)' }
                          : { background: 'rgba(255,255,255,0.04)', color: '#A7A3C4', border: '1px solid rgba(255,255,255,0.07)' }}
                      >
                        {f}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                  {filteredListings.map((listing) => (
                    <div key={listing.id} className="neon-card p-6 flex flex-col gap-4">
                      <div className="flex items-start justify-between">
                        <span
                          className="text-[11px] font-medium px-2 py-0.5 rounded"
                          style={{ background: `${listing.accent}10`, color: listing.accent, border: `1px solid ${listing.accent}20` }}
                        >
                          {listing.type}
                        </span>
                        <span
                          className="text-[11px] font-medium px-2 py-0.5 rounded"
                          style={{ background: 'rgba(255,255,255,0.04)', color: listing.statusAccent, border: `1px solid ${listing.statusAccent}30` }}
                        >
                          {listing.status}
                        </span>
                      </div>
                      <div>
                        <h3 className="font-display font-semibold text-[16px] mb-1" style={{ color: '#F2F4FF' }}>
                          {listing.label}
                        </h3>
                        <p className="text-[13px]" style={{ color: '#A7A3C4' }}>{listing.summary}</p>
                      </div>
                      <div className="font-display font-bold text-[22px]" style={{ color: listing.accent }}>
                        {listing.price}
                      </div>
                      <button
                        className="w-full py-2.5 text-[13px] font-medium transition-all duration-150 rounded-lg"
                        style={{ border: '1px solid rgba(255,255,255,0.10)', color: '#A7A3C4', background: 'rgba(255,255,255,0.04)' }}
                      >
                        Inquire About This Unit
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* Resident types */}
            <section className="py-16" style={{ background: '#0C0A16' }}>
              <div className="neon-divider mb-0" />
              <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 pt-16">
                <div className="text-center mb-12">
                  <h2 className="font-display font-bold text-[32px] sm:text-[38px] leading-tight tracking-tight mb-4" style={{ color: '#F2F4FF' }}>
                    Panorama appeals to different kinds of residents.
                  </h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                  {residentTypes.map((rt) => (
                    <div key={rt.id} className="neon-card p-7">
                      <div
                        className="w-10 h-10 rounded-xl flex items-center justify-center mb-5"
                        style={{ background: `${rt.accent}12`, border: `1px solid ${rt.accent}22` }}
                      >
                        <rt.icon size={18} style={{ color: rt.accent }} />
                      </div>
                      <h3 className="font-display font-semibold text-[17px] mb-3" style={{ color: '#F2F4FF' }}>
                        {rt.title}
                      </h3>
                      <p className="text-[13.5px] leading-relaxed" style={{ color: '#A7A3C4' }}>
                        {rt.body}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* Townhomes section */}
            <section className="py-16" style={{ background: '#07060C' }}>
              <div className="neon-divider mb-0" />
              <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 pt-16">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                  <div>
                    <p className="text-[12px] font-semibold tracking-[0.12em] uppercase mb-3" style={{ color: '#FF3DF5' }}>
                      Townhomes at Panorama
                    </p>
                    <h2 className="font-display font-bold text-[32px] sm:text-[38px] leading-tight tracking-tight mb-5" style={{ color: '#F2F4FF' }}>
                      A different scale within the same community.
                    </h2>
                    <p className="text-[16px] leading-relaxed mb-5" style={{ color: 'rgba(167,163,196,0.90)' }}>
                      Panorama includes a collection of townhome residences alongside the tower units. Townhomes offer a distinct living experience — private entry, multi-level layouts, and a different relationship with the building — while still providing full access to all shared amenities.
                    </p>
                    <p className="text-[15px] leading-relaxed mb-8" style={{ color: 'rgba(167,163,196,0.75)' }}>
                      For buyers who want more space, a private entrance, or a ground-level lifestyle within a premium community, the townhomes represent a compelling alternative to tower living.
                    </p>
                    <Link href="/amenities" className="btn-secondary">
                      Explore Amenities
                    </Link>
                  </div>
                  <div
                    className="relative overflow-hidden panorama-premium-bg h-[360px] flex items-end"
                    style={{ borderRadius: '12px', border: '1px solid rgba(255,255,255,0.08)' }}
                  >
                    <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse 80% 70% at 50% 50%, rgba(255,61,245,0.08) 0%, transparent 70%)' }} />
                    <div className="relative z-10 p-8 w-full">
                      <div className="glass-card-dark p-5" style={{ borderRadius: '10px' }}>
                        <div className="grid grid-cols-3 gap-4 text-center">
                          {[
                            { value: 'Ground', label: 'Level Entry', accent: '#FF3DF5' },
                            { value: 'Multi', label: 'Level Layouts', accent: '#7A5CFF' },
                            { value: 'Full', label: 'Amenity Access', accent: '#4CC9FF' },
                          ].map((stat) => (
                            <div key={stat.label}>
                              <div className="font-display font-bold text-[18px]" style={{ color: stat.accent }}>{stat.value}</div>
                              <div className="text-[11px] mt-0.5" style={{ color: 'rgba(167,163,196,0.80)' }}>{stat.label}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Specialist spotlight */}
            <section className="py-16" style={{ background: '#0C0A16' }}>
              <div className="neon-divider mb-0" />
              <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 pt-16">
                <div className="text-center mb-10">
                  <p className="text-[12px] font-semibold tracking-[0.12em] uppercase mb-3" style={{ color: '#7A5CFF' }}>
                    Specialist Spotlight
                  </p>
                  <h2 className="font-display font-bold text-[28px] sm:text-[34px] leading-tight tracking-tight mb-3" style={{ color: '#F2F4FF' }}>
                    Thinking of buying, leasing, or selling?
                  </h2>
                  <p className="text-[15px] max-w-xl mx-auto" style={{ color: '#A7A3C4' }}>
                    Connect with a specialist who knows Panorama. This section will feature Panorama-familiar real estate professionals.
                  </p>
                </div>
                <div className="max-w-2xl mx-auto">
                  <div
                    className="neon-card p-8 text-center"
                    style={{ border: '1px solid rgba(122,92,255,0.20)' }}
                  >
                    <div
                      className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-5"
                      style={{ background: 'rgba(122,92,255,0.12)', border: '1px solid rgba(122,92,255,0.25)' }}
                    >
                      <Users size={24} style={{ color: '#7A5CFF' }} />
                    </div>
                    <h3 className="font-display font-semibold text-[18px] mb-2" style={{ color: '#F2F4FF' }}>
                      Panorama Real Estate Specialist
                    </h3>
                    <p className="text-[13.5px] leading-relaxed mb-6 max-w-md mx-auto" style={{ color: '#A7A3C4' }}>
                      This spotlight will feature a Panorama-familiar luxury home specialist. Specialists listed here have direct knowledge of the building, its units, and the community.
                    </p>
                    <div className="flex flex-wrap items-center justify-center gap-3">
                      <button
                        className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-[13px] font-medium transition-all duration-150"
                        style={{ background: 'rgba(122,92,255,0.10)', color: '#7A5CFF', border: '1px solid rgba(122,92,255,0.22)' }}
                      >
                        <Phone size={14} />
                        Contact Specialist
                      </button>
                      <button
                        className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-[13px] font-medium transition-all duration-150"
                        style={{ background: 'rgba(255,255,255,0.04)', color: '#A7A3C4', border: '1px solid rgba(255,255,255,0.08)' }}
                      >
                        <Mail size={14} />
                        Send Inquiry
                      </button>
                    </div>
                    <p className="text-[11px] mt-5" style={{ color: 'rgba(167,163,196,0.35)' }}>
                      Specialist listings are informational. This platform does not imply endorsement or represent any brokerage.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* CTA */}
            <section className="py-20" style={{ background: '#07060C' }}>
              <div className="neon-divider mb-0" />
              <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 pt-20 text-center">
                <h2 className="font-display font-bold text-[32px] sm:text-[40px] leading-tight tracking-tight mb-4" style={{ color: '#F2F4FF' }}>
                  Interested in Panorama?
                </h2>
                <p className="text-[16px] max-w-xl mx-auto mb-8" style={{ color: '#A7A3C4' }}>
                  Request access to the private resident community or learn more about what Panorama has to offer.
                </p>
                <div className="flex flex-wrap items-center justify-center gap-4">
                  <Link href="/request-access" className="btn-primary group">
                    Request Access
                    <ArrowRight size={15} className="transition-transform duration-200 group-hover:translate-x-0.5" />
                  </Link>
                  <Link href="/faq" className="btn-secondary">
                    Read the FAQ
                  </Link>
                </div>

                {/* Cross-links */}
                <div
                  className="mt-14 pt-10 border-t"
                  style={{ borderColor: 'rgba(255,255,255,0.06)' }}
                >
                  <p className="text-[11px] font-semibold tracking-[0.10em] uppercase mb-5" style={{ color: 'rgba(167,163,196,0.35)' }}>
                    Explore more
                  </p>
                  <div className="flex flex-wrap items-center justify-center gap-3">
                    <Link href="/why-panorama" className="text-[13px] font-medium px-4 py-2 rounded-lg transition-all duration-150 hover:bg-white/[0.06]" style={{ color: '#A7A3C4', border: '1px solid rgba(255,255,255,0.08)' }}>
                      Why Panorama
                    </Link>
                    <Link href="/amenities" className="text-[13px] font-medium px-4 py-2 rounded-lg transition-all duration-150 hover:bg-white/[0.06]" style={{ color: '#A7A3C4', border: '1px solid rgba(255,255,255,0.08)' }}>
                      Amenities
                    </Link>
                    <Link href="/lifestyle" className="text-[13px] font-medium px-4 py-2 rounded-lg transition-all duration-150 hover:bg-white/[0.06]" style={{ color: '#A7A3C4', border: '1px solid rgba(255,255,255,0.08)' }}>
                      The Lifestyle
                    </Link>
                    <Link href="/community" className="text-[13px] font-medium px-4 py-2 rounded-lg transition-all duration-150 hover:bg-white/[0.06]" style={{ color: '#A7A3C4', border: '1px solid rgba(255,255,255,0.08)' }}>
                      The Community
                    </Link>
                    <Link href="/nearby" className="text-[13px] font-medium px-4 py-2 rounded-lg transition-all duration-150 hover:bg-white/[0.06]" style={{ color: '#A7A3C4', border: '1px solid rgba(255,255,255,0.08)' }}>
                      What's Nearby
                    </Link>
                  </div>
                </div>
              </div>
            </section>
          </>
        )}
      </main>
      <SiteFooter />
    </div>
  );
}
